import type { Classification, Layer } from "../../types/projects";

/**
 * Discriminator for the Files-tab action menu. Stage 8.7 simplified
 * the matrix per the locked architectural principle: the Files tab
 * routes files INTO candidate pools, the Workflow tab visualizes the
 * pools and assigns specific candidates. There are now three pool
 * destinations (site_plan, module, inverter) plus one disabled
 * placeholder (tracker, v1.1).
 *
 * Keeping the state machine in its own module (instead of inlined into
 * the row component) makes it unit-testable and the row renderer
 * becomes a trivial switch.
 */
export type FileRowState =
  | "classification-pending"
  | "site-plan-routable"
  | "site-plan-routed"
  | "extractable-pdf-module"
  | "extractable-pdf-inverter"
  | "extractable-pdf-tracker"
  | "linked-component"
  | "classification-failed"
  | "classification-unknown"
  | "other-file";

export type FileAction =
  | "add-to-site-plan-candidates"
  | "add-to-module-candidates"
  | "add-to-inverter-candidates"
  | "add-to-tracker-candidates"
  | "view-in-workflow"
  | "view-component"
  | "unlink"
  | "unlink-from-site-plan"
  | "reclassify"
  | "reclassify-with-hints"
  | "retry-system-error"
  | "just-store"
  | "delete";

export interface FileRowActions {
  state: FileRowState;
  primary: FileAction | null;
  /** Set when the primary action exists but the user cannot invoke it
   * (currently only the v1.1 tracker placeholder). The button is still
   * rendered but with a tooltip explaining why. */
  primaryDisabledReason?: string;
  overflow: FileAction[];
}

/** A boundary candidate is a KMZ/KML/GeoJSON with at least one polygon. */
function isGeometryFile(layer: Layer): boolean {
  const name = (layer.original_filename || layer.name || "").toLowerCase();
  return (
    name.endsWith(".kmz") ||
    name.endsWith(".kml") ||
    name.endsWith(".geojson") ||
    name.endsWith(".json")
  );
}

function isPdf(layer: Layer): boolean {
  return (layer.original_filename || layer.name || "")
    .toLowerCase()
    .endsWith(".pdf");
}

/**
 * Decide the state-machine state + which actions to surface.
 *
 * Stage 8.7 routing matrix (locked):
 *
 * | classification           | primary                          | overflow                                       |
 * |--------------------------|----------------------------------|------------------------------------------------|
 * | geometry (any type)      | Add to site plan candidates      | Reclassify · Delete                            |
 * | geometry, already routed | Go to Studies →               | Unlink from site plan · Reclassify · Delete    |
 * | PDF, module              | Add to module candidates         | Reclassify · Delete                            |
 * | PDF, inverter            | Add to inverter candidates       | Reclassify · Delete                            |
 * | PDF, tracker             | Add to tracker candidates (off)  | Reclassify · Delete                            |
 * | PDF, classification null | (none — analyzing)               | (none)                                         |
 * | PDF, other / unknown     | (none)                           | Reclassify · Reclassify with hints · Just store · Delete |
 * | linked component         | Go to Studies →               | Unlink · Delete                                |
 * | classification system err| Retry classification             | Reclassify with hints · Delete                 |
 * | other-file (DWG etc.)    | (none)                           | Delete                                         |
 */
export function computeFileRowActions(
  layer: Layer,
  options: {
    /** Caller wires this in when the layer's classification produced a
     * component link (e.g. ``tenant_component_links.source_layer_id``
     * matches). Stage 8.7 leaves this hardcoded false for the PDF side
     * until a list-links-by-layer endpoint lands. */
    isLinkedComponent?: boolean;
    /** Set true when at least one site_plan_candidate exists with
     * ``layer_id`` matching this layer. The Files tab passes this in
     * by querying ``useSitePlanCandidates`` and reducing. */
    isInSitePlanPool?: boolean;
    /** Set true when the layer is in the middle of a classify task. */
    classificationPending?: boolean;
    /** Set true when the last reclassify call returned outcome=system_error. */
    classificationSystemError?: boolean;
  },
): FileRowActions {
  const {
    isLinkedComponent = false,
    isInSitePlanPool = false,
    classificationPending = false,
    classificationSystemError = false,
  } = options;

  if (isLinkedComponent) {
    return {
      state: "linked-component",
      primary: "view-in-workflow",
      overflow: ["unlink", "delete"],
    };
  }

  if (classificationPending) {
    return {
      state: "classification-pending",
      primary: null,
      overflow: [],
    };
  }

  if (classificationSystemError) {
    return {
      state: "classification-failed",
      primary: "retry-system-error",
      overflow: ["reclassify-with-hints", "delete"],
    };
  }

  const klass: Classification | null = layer.classification ?? null;

  if (isPdf(layer)) {
    if (klass === "module") {
      return {
        state: "extractable-pdf-module",
        primary: "add-to-module-candidates",
        overflow: ["reclassify", "delete"],
      };
    }
    if (klass === "inverter") {
      return {
        state: "extractable-pdf-inverter",
        primary: "add-to-inverter-candidates",
        overflow: ["reclassify", "delete"],
      };
    }
    if (klass === "tracker") {
      return {
        state: "extractable-pdf-tracker",
        primary: "add-to-tracker-candidates",
        primaryDisabledReason: "Tracker extraction available in v1.1",
        overflow: ["reclassify", "delete"],
      };
    }
    if (klass === null) {
      return {
        state: "classification-pending",
        primary: null,
        overflow: [],
      };
    }
    // unknown / other
    return {
      state: "classification-unknown",
      primary: null,
      overflow: ["reclassify", "reclassify-with-hints", "just-store", "delete"],
    };
  }

  if (isGeometryFile(layer)) {
    if (isInSitePlanPool) {
      return {
        state: "site-plan-routed",
        primary: "view-in-workflow",
        overflow: ["unlink-from-site-plan", "reclassify", "delete"],
      };
    }
    return {
      state: "site-plan-routable",
      primary: "add-to-site-plan-candidates",
      overflow: ["reclassify", "delete"],
    };
  }

  return {
    state: "other-file",
    primary: null,
    overflow: ["delete"],
  };
}

export const ACTION_LABELS: Record<FileAction, string> = {
  "add-to-site-plan-candidates": "Add to site plan candidates",
  "add-to-module-candidates": "Add to module candidates",
  "add-to-inverter-candidates": "Add to inverter candidates",
  "add-to-tracker-candidates": "Add to tracker candidates",
  "view-in-workflow": "Go to Studies →",
  "view-component": "View component",
  unlink: "Unlink from project",
  "unlink-from-site-plan": "Unlink from site plan",
  reclassify: "Reclassify",
  "reclassify-with-hints": "Reclassify with hints",
  "retry-system-error": "Retry classification",
  "just-store": "Just store as file",
  delete: "Delete",
};
